import { useEffect, useState } from "react";
import { Button, ColorV2, FAQs, Text } from "@adoptaunabuelo/react-components";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import styled from "styled-components";
import media from "styled-media-query";
import { State } from "../../redux";

import ContainerSection from "../../components/landing/ContainerSection";
import PriceBreakdownCell from "../../components/cell/PriceBreakdownCell";
import { Play } from "lucide-react";
import VideoModal from "../../components/modal/VideoModal";
import NumberIcon from "../../assets/images/icon/Number";
import { ProductScheme } from "client-v2";
import EscrituraIcon from "../../assets/logo/Escritura";

const Container = styled.div`
	display: flex;
	flex: 1;
	flex-direction: column;
`;
const BackgroundSection = styled.div`
	display: flex;
	justify-content: flex-end;
	padding: 56px 48px;
	border-radius: 32px;
	width: 100%;
	height: 500px;
	align-items: center;
	background-repeat: no-repeat;
	background-size: cover;
	background-position: center;
	background-position-y: center;
	${media.lessThan("medium")`
       width: unset;
	   height: unset;
	   padding: 0px;
	`}
`;
const Row = styled.div`
	display: flex;
	flex-direction: row;
	${media.lessThan("medium")`
       flex-direction: column;
	`}
`;
const Col = styled.div`
	display: flex;
	flex-direction: column;
`;
const PackContainer = styled.div`
	display: grid;
	grid-template-columns: repeat(10, 1fr);
	grid-template-rows: repeat(5, 1fr);
	grid-column-gap: 16px;
	grid-row-gap: 16px;
	overflow-x: scroll;
	-ms-overflow-style: none;
	scrollbar-width: none;
	::-webkit-scrollbar {
		display: none;
	}
	${media.lessThan("medium")`
    	grid-template-columns: repeat(4, 1fr);
		grid-template-rows: repeat(2, 1fr);
		grid-column-gap: 8px;
		grid-row-gap: 8px;
		max-width: calc(100vw - 48px);
		margin: 0px -48px;
		padding: 0px 24px;
	`}
`;
const PackCell = styled.div<{ $backgroundImage: string }>`
	display: flex;
	flex: 1;
	flex-direction: column;
	background-color: ${ColorV2.surface.background};
	border-radius: 24px;
	padding: 24px;
	background-repeat: no-repeat;
	background-size: cover;
	background-position: center;
	background-position-y: center;
	background-image: url(${(props) => props.$backgroundImage});
	min-height: 219px;
	${media.lessThan("medium")`
		width: 244px;
    	min-height: 176px;
		max-height: 400px;
	`}
`;
const VideoContainer = styled.div`
	display: flex;
	aspect-ratio: 9/16;
	background-color: ${ColorV2.surface.background};
	border-radius: 24px;
	height: 522px;
	width: fit-content;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	background-position: center;
	background-repeat: no-repeat;
	background-size: cover;
	${media.lessThan("medium")`
		aspect-ratio: 1/1;
		width: 100%;
		height: auto;
	`}
`;
const VideoPlayButton = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	height: 64px;
	width: 64px;
	border-radius: 32px;
	background-color: rgba(0, 29, 61, 0.14);
	backdrop-filter: blur(10px);
`;
const PackImage = styled.img`
	height: 416px;
	width: auto;
	${media.lessThan("medium")`
		width: 100%;
		height: auto;
		margin-top: 32px;
	`}
`;

const Info = (props: InfoProps) => {
	const navigate = useNavigate();
	const isMobile = useSelector((state: State) => state.screen.isMobile);
	const [play, setPlay] = useState(false);
	const [currency, setCurrency] = useState("€");
	const [price, setPrice] = useState(0);
	const [ambassadorPrice, setAmbassadorPrice] = useState(0);

	const steps = [
		{
			img: <NumberIcon type="1" color={ColorV2.text.neutralHard} />,
			title: "Compra tu Pack Solidario",
			subtitle:
				"Te lo enviaremos a la dirección que nos indiques. Recuerda que con cada compra nos estás ayudando a continuar nuestra misión de acompañar a personas mayores.",
		},
		{
			img: <NumberIcon type="2" color={ColorV2.text.neutralHard} />,
			title: "Escribe tu carta",
			subtitle:
				"Con todo lo que incluye el pack, redacta un mensaje de cariño y amor para felicitar la Navidad.",
		},
		{
			img: <NumberIcon type="3" color={ColorV2.text.neutralHard} />,
			title: "Envíanos tu carta",
			subtitle:
				"El sobre ya está listo para enviar. Nosotros nos encargaremos de hacérsela llegar a la persona mayor.",
		},
	];

	const distribution = [
		{
			title: "Coste del Pack",
			amount: {
				eur: 8.71,
			},
		},
		{
			title: "Comisión bancaria ",
			amount: {
				eur: 0.66,
			},
		},
		{
			title: "Envío de ida y vuelta",
			amount: {
				eur: 6.31,
			},
		},
		{
			title: "Donación a los programas sociales de la Fundación",

			amount: {
				eur: 8.29,
			},
		},
		{
			title: "Impuestos 21% IVA",

			amount: {
				eur: 5.03,
			},
		},
	];

	const faqs = [
		{
			id: "item-1",
			title: "¿Hasta cuándo puedo escribir mi carta?",
			description:
				"Puedes enviarnos tu carta hasta el 20 de diciembre, que es el último día de recepción.",
		},
		{
			id: "item-2",
			title: "¿Dónde envío mi carta?",
			description:
				"No te preocupes, dentro del pack encontrarás las instrucciones detalladas. Solo tendrás que llevar tu carta a cualquier oficina de Correos. El sello ya está incluido, así que no tendrás que abonar nada, simplemente entrégalo allí y nosotros nos encargaremos del resto.",
		},
		{
			id: "item-3",
			title: "¿Si vivo fuera de España, puedo comprar un pack?",
			description:
				"Lo sentimos, pero este año la iniciativa está disponible solo en España.\nNos encantaría poder llegar más lejos y esperamos que el año que viene podamos hacerlo en muchos más países.",
		},
		{
			id: "item-4",
			title: "¿Puedo visitar a mi abuelo?",
			description:
				"No podemos asegurarte que podrás mantener el contacto con tu abuelo. Si quieres ser voluntario, descarga nuestra app y completa el proceso. Una vez hecho, nuestro equipo te explicará los siguientes pasos.",
		},
		{
			id: "item-5",
			title: "¿Puedo comprar más de un pack?",
			description:
				"¡Claro! Puedes comprar todos los que quieras.\nCada pack incluye la ficha de una persona mayor diferente, para que puedas escribir a más de uno si lo deseas.",
		},
		{
			id: "item-6",
			title: "Soy un colegio o empresa y quiero colaborar",
			description:
				"¡Genial! Escribe a info@adoptaunabuelo.org y te enviaremos toda la información para adquirir tu pack solidario.",
		},
	];

	useEffect(() => {
		const tempCurrency = "EUR";
		setCurrency("€");
		setPrice(
			(props.product.data && props.product.data[tempCurrency].price) || 0
		);
		setAmbassadorPrice(
			(props.product.data &&
				props.product.data[tempCurrency].ambassadorPrice) ||
				0
		);
	}, []);

	const onVideoEnd = () => {
		setPlay(false);
	};

	return (
		<Container>
			<VideoModal
				isVisible={play}
				url={"https://www.youtube.com/shorts/fVWCaMleQqc"}
				onFinish={onVideoEnd}
				onClose={() => setPlay(false)}
			/>
			{/*Problema*/}
			<ContainerSection
				style={{
					justifyContent: "center",
					backgroundColor: ColorV2.surface.background,
					gap: isMobile ? 0 : 32,
					paddingTop: isMobile ? undefined : 0,
					paddingBottom: 0,
					flexDirection: isMobile ? "column-reverse" : "row",
				}}
			>
				<Col
					style={{
						marginTop: 64,
						flex: 1,
						backgroundImage: `url(${require("../../assets/images/background/letter.webp")})`,
						backgroundSize: "contain",
						backgroundPosition: "center",
						backgroundPositionY: "bottom",
						backgroundRepeat: "no-repeat",
						minHeight: 342,
					}}
				></Col>
				<Col
					style={{
						flex: 1,
						maxWidth: 490,
						height: 600,
					}}
				>
					<Col style={{ flex: 1, justifyContent: "center" }}>
						<Text type="h2" weight="semibold">
							Una forma única de felicitar la Navidad
						</Text>
						<Text
							type="h6"
							style={{
								marginTop: 16,
							}}
						>
							Con la compra del Pack Solidario de Escritura nos
							ayudas a{" "}
							<span style={{ fontWeight: "600" }}>
								enviar miles de felicitaciones
							</span>{" "}
							de Navidad y colaboras para que la fundación pueda{" "}
							<span style={{ fontWeight: "600" }}>
								seguir acompañando a personas mayores
							</span>{" "}
							en 2026.
						</Text>
					</Col>
					<Text
						type="c1"
						style={{
							marginBottom: isMobile ? 0 : 64,
							marginTop: isMobile ? 32 : 0,
							color: ColorV2.text.neutralMedium,
						}}
					>
						Ezequiela recibiendo su carta de Navidad
					</Text>
				</Col>
			</ContainerSection>
			{/*¿Qué incluye el pack?*/}
			<ContainerSection>
				<Col
					style={{
						flex: 1,
						alignItems: "center",
						justifyContent: "center",
					}}
				>
					<Text
						type="h2"
						weight="semibold"
						style={{ marginBottom: 24 }}
					>
						¿Qué incluye este Pack Solidario?
					</Text>
					<Text
						type="h6"
						style={{ marginBottom: isMobile ? 48 : 64 }}
					>
						Todo lo que necesitas para escribir una carta llena de
						cariño a una persona mayor
					</Text>
					<PackContainer>
						<PackCell
							$backgroundImage={require("../../assets/images/pack/hojas.webp")}
							style={{
								gridArea: isMobile
									? "1 / 1 / 3 / 2"
									: "1 / 1 / 4 / 4",
								backgroundSize: "contain",
								backgroundPositionY: "bottom",
							}}
						>
							<Text type="h6" weight="medium">
								Folios
							</Text>
							<Text type="p2">
								Donde escribir tu carta o añadir algún dibujo,
								¡no hay límites para la creatividad!
							</Text>
						</PackCell>
						<PackCell
							$backgroundImage={require("../../assets/images/pack/boligrafo.webp")}
							style={{
								gridArea: isMobile
									? "1 / 2 / 2 / 3"
									: "1 / 4 / 3 / 7",
								justifyContent: "flex-end",
							}}
						>
							<Text type="h6" weight="medium">
								Bolígrafo
							</Text>
							<Text type="p2">
								Para escribir con mucho cariño y guardarlo como
								recuerdo.
							</Text>
						</PackCell>
						<PackCell
							$backgroundImage={require("../../assets/images/pack/sobre.webp")}
							style={{
								gridArea: isMobile
									? "2 / 2 / 3 / 3"
									: "1 / 7 / 3 / 11",
								alignItems: "flex-end",
								backgroundSize: "contain",
								backgroundPositionY: "bottom",
							}}
						>
							<div style={{ maxWidth: isMobile ? "unset" : 220 }}>
								<Text type="h6" weight="medium">
									Sobre
								</Text>
								<Text type="p2">
									Que llegará directamente a manos de la
									persona mayor.
								</Text>
							</div>
						</PackCell>
						<PackCell
							$backgroundImage={require("../../assets/images/pack/colores.webp")}
							style={{
								gridArea: isMobile
									? "1 / 3 / 2 / 4"
									: "4 / 1 / 6 / 4",
								justifyContent: "flex-end",
								alignItems: "flex-end",
							}}
						>
							<div style={{ maxWidth: isMobile ? "unset" : 220 }}>
								<Text type="h6" weight="medium">
									Caja de pinturas
								</Text>
								<Text type="p2">
									Para que los más pequeños de la familia
									puedan dibujar.
								</Text>
							</div>
						</PackCell>
						<PackCell
							$backgroundImage={require("../../assets/images/pack/pegatinas.webp")}
							style={{
								gridArea: isMobile
									? "1 / 4 / 3 / 5"
									: "3 / 4 / 6 / 8",
								justifyContent: "flex-end",
								alignItems: "flex-end",
								backgroundPosition: "left",
							}}
						>
							<div style={{ maxWidth: isMobile ? "unset" : 200 }}>
								<Text type="h6" weight="medium">
									Set de pegatinas
								</Text>
								<Text type="p2">
									Para decorar y ponerle el toque final a la
									carta ¡o para usarlas a tu gusto!
								</Text>
							</div>
						</PackCell>
						<PackCell
							$backgroundImage={
								isMobile
									? undefined
									: require("../../assets/images/pack/instrucciones.webp")
							}
							style={{
								backgroundSize: "contain",
								backgroundPosition: "left",
								backgroundPositionY: "bottom",
								gridArea: isMobile
									? "2 / 3 / 3 / 4"
									: "3 / 8 / 6 / 11",
							}}
						>
							<Text type="h6" weight="medium">
								Ficha de la persona mayor
							</Text>
							<Text type="p2">
								Con información de la persona mayor a la que irá
								destinada para que tu carta sea personalizada y
								con las instrucciones de envío.
							</Text>
						</PackCell>
					</PackContainer>
				</Col>
			</ContainerSection>
			{/*Transparencia*/}
			<ContainerSection
				style={{
					gap: isMobile ? 32 : 85,
				}}
			>
				<Col style={{ flex: 1, justifyContent: "center" }}>
					<Text type="h2" weight="semibold">
						Somos transparentes. Queremos que sepas a dónde va cada
						euro
					</Text>
					<Row style={{ marginTop: isMobile ? 24 : 48 }}>
						<Col>
							<Text type="h6" weight="medium">
								Pack Solidario <br />
								de{" "}
								<EscrituraIcon
									height={20}
									width={75}
									style={{ verticalAlign: "middle" }}
								/>
							</Text>
							<Text type="h1" weight="semibold">
								{(price * 1.21).toFixed(0)}
								<span
									style={{ fontSize: 28, lineHeight: "36px" }}
								>
									{currency}
								</span>
							</Text>
							<Text
								type="p"
								weight="medium"
								style={{
									backgroundColor: ColorV2.surface.primary,
									color: "white",
									width: "max-content",
									borderRadius: 4,
									padding: "0px 6px",
									marginTop: 8,
								}}
							>
								{(ambassadorPrice * 1.21).toFixed(0)} {currency}{" "}
								para embajadores
							</Text>
						</Col>
						<PackImage
							src={require("../../assets/images/pack/pack.webp")}
						/>
					</Row>
				</Col>
				<Col style={{ flex: 1, justifyContent: "center" }}>
					<PriceBreakdownCell
						style={{ padding: 24 }}
						options={distribution}
					/>
				</Col>
			</ContainerSection>
			{/*Escribe una carta con amigos*/}
			<ContainerSection
				style={{
					backgroundColor: isMobile
						? ColorV2.surface.background
						: "white",
				}}
			>
				<BackgroundSection
					style={{
						backgroundImage: isMobile
							? "unset"
							: `url(${require("../../assets/images/pack/header-02.webp")})`,
					}}
				>
					<Col
						style={{
							flex: 1,
							maxWidth: isMobile ? "unset" : "440px",
						}}
					>
						{isMobile && (
							<img
								style={{
									height: 280,
									width: "100%",
									objectFit: "cover",
									borderRadius: 24,
									marginBottom: 32,
									objectPosition: "left",
								}}
								src={require("../../assets/images/pack/header-02.webp")}
							/>
						)}
						<Text
							type="h2"
							weight="semibold"
							style={{
								color: isMobile
									? ColorV2.text.neutralHard
									: "white",
								marginBottom: 24,
							}}
						>
							Una carta escrita a mano llega directa al corazón
						</Text>
						<Text
							type="h6"
							style={{
								color: isMobile
									? ColorV2.text.neutralHard
									: "white",
							}}
						>
							Escribe tu carta en familia, en pareja, tú solo o
							con amigos.
						</Text>
					</Col>
				</BackgroundSection>
			</ContainerSection>
			{/*Cómo enviar tu carta*/}
			<ContainerSection
				style={{
					flexDirection: "column",
				}}
			>
				<Text
					type="h2"
					weight="semibold"
					style={{ textAlign: "center" }}
				>
					Cómo enviar tu carta física esta Navidad
				</Text>
				<Text
					type="h6"
					style={{
						textAlign: "center",
						marginBottom: 64,
					}}
				>
					Felicita la Navidad a una persona mayor siguiendo estos
					sencillos pasos
				</Text>
				<Row style={{ gap: 32 }}>
					{steps.map((item, index) => (
						<Col
							key={"step" + index}
							style={{
								flex: 1,
								alignItems: isMobile ? "flex-start" : "center",
							}}
						>
							{item.img}
							<Text
								type="h5"
								weight="medium"
								style={{
									marginTop: 12,
									marginBottom: 8,
									textAlign: isMobile ? "left" : "center",
								}}
							>
								{item.title}
							</Text>
							<Text
								type="p"
								style={{
									color: ColorV2.text.neutralMedium,
									textAlign: isMobile ? "left" : "center",
								}}
							>
								{item.subtitle}
							</Text>
						</Col>
					))}
				</Row>
			</ContainerSection>
			{/*Cierre*/}
			<ContainerSection style={{ gap: 16, alignItems: "center" }}>
				<Col style={{ flex: 1 }}>
					<Text
						type="h2"
						weight="semibold"
						style={{ marginBottom: 16 }}
					>
						Una imagen vale más que mil palabras
					</Text>
					<Text type="h6">
						Estas son algunas de las reacciones de las personas
						mayores al leer vuestras cartas.
					</Text>
					{!isMobile && (
						<Button
							style={{
								width: "fit-content",
								display: "flex",
								marginTop: 48,
							}}
							onClick={() =>
								navigate(
									`/shop/?id=${props.product.objectId}`,
									{
										state: {
											product: props.product,
											view: props.view,
										},
									}
								)
							}
						>
							Quiero mi Pack Solidario
						</Button>
					)}
				</Col>
				<Col style={{ flex: 1, alignItems: "center", width: "100%" }}>
					<VideoContainer
						style={{
							backgroundImage: `url(${require("../../assets/images/pack/video-thumb.webp")})`,
						}}
						onClick={() => setPlay(true)}
					>
						<VideoPlayButton>
							<Play color="white" fill="white" />
						</VideoPlayButton>
					</VideoContainer>
				</Col>
			</ContainerSection>
			{/*FAQs*/}
			<ContainerSection
				style={{ gap: 85, backgroundColor: ColorV2.surface.background }}
			>
				<Col style={{ flex: 1 }}>
					<Text
						type="h2"
						weight="semibold"
						style={{ marginBottom: 24 }}
					>
						¡No te quedes con dudas!
					</Text>
					<Text type="h6">
						Si la pregunta que tienes no está en la lista, puedes
						escribirnos a info@adoptaunabuelo.org y una persona del
						equipo de responderá.
					</Text>
				</Col>
				<FAQs style={{ flex: 1 }} options={faqs} />
			</ContainerSection>
		</Container>
	);
};
export default Info;
export interface InfoProps {
	product: ProductScheme;
	view: string;
}
